// App.js - Updated with IndexedDB for thread storage
import React, { useState, useEffect, useCallback, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import TopicList from "./components/TopicList";
import { initializeFromCsv } from './initPromptLibrary';
import ChatDialog from "./components/ChatDialog";
import SearchBar from "./components/SearchBar";
import Settings from "./components/Settings";
import ContextWarning from "./components/ContextWarning";
import ResourceWarning from "./components/ResourceWarning";
import InstallPrompt from "./components/InstallPrompt"; 
import StorageStats from "./components/StorageStats"; // Import the new StorageStats component
import Spinner from "./components/Spinner";
import { decryptData } from "./encryptionUtils";
import { cleanupThreadFileReferences } from './fileUtils/threadCleanup';
import { 
  selectMessagesForContext, 
  isApproachingContextLimit,
  getContextUtilizationPercentage
} from "./contextUtils";
import apiUsageTracker from "./apiUsageTracker";
import "./App.css";
import { Anthropic } from "@anthropic-ai/sdk";
import { prepareFilesForAnthropicApi, getPdfSupportedModel, needsPdfBetaHeader } from './fileUtils/pdfUtils';
import { processFiles, formatFileContentForClaude } from './fileUtils/fileProcessingUtils';
import { cleanFilesForStorage } from './fileUtils/cleanFileUtils';
import threadService from './services/threadService'; // Import the thread service for IndexedDB operations

// Constants for auto-continuation
const MAX_CONTINUATIONS = 4;
const RETRY_DELAYS = [1000, 2000, 4000]; // Delays in milliseconds
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Helper component to monitor trial status and fix the "API key required" race condition
function TrialStatusMonitor({ onCloseSettings }) {
  // Track state to avoid responding to normal settings opens
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  
  // Listen for the settings dialog opening
  useEffect(() => {
    // This function handles when settings is opened
    const handleSettingsOpened = (event) => {
      // ONLY intercept settings opened for API key required reason, not manual opens
      if (event.detail && event.detail.reason === 'apiKeyRequired') {
        console.log("TrialStatusMonitor: Detected settings opening with apiKeyRequired reason");
        setApiKeyDialogOpen(true);
        
        // Check if trial is active in localStorage first (fastest check)
        const storedTrial = localStorage.getItem('freeTrialData');
        if (storedTrial) {
          try {
            const parsedData = JSON.parse(storedTrial);
            // Only auto-close if trial is active AND has remaining prompts
            if (parsedData.active && parsedData.remaining > 0) {
              console.log("TrialStatusMonitor: Trial is active with remaining prompts, intercepting settings open");
              
              // Close settings through the provided onCloseSettings callback
              setTimeout(() => {
                if (onCloseSettings) {
                  console.log("TrialStatusMonitor: Closing settings because trial is active");
                  onCloseSettings();
                  setApiKeyDialogOpen(false);
                }
              }, 100);
              
              return;
            } else {
              console.log("TrialStatusMonitor: Trial inactive or has no remaining prompts, keeping settings open");
            }
          } catch (err) {
            console.error("TrialStatusMonitor: Error parsing trial data:", err);
          }
        }
        
        // If no conclusive data in localStorage, check with FreeTrialManager
        try {
          const FreeTrialManager = require('./components/FreeTrialManager').default;
          // Check if trial is active AND has remaining prompts
          if (FreeTrialManager.isTrialActive() && FreeTrialManager.getRemainingPromptCount() > 0) {
            console.log("TrialStatusMonitor: Trial is active from FreeTrialManager with remaining prompts, intercepting settings open");
            
            // Give a bit more time for this check
            setTimeout(() => {
              if (onCloseSettings) {
                console.log("TrialStatusMonitor: Closing settings because trial is active");
                onCloseSettings();
              }
            }, 200);
          } else {
            console.log("TrialStatusMonitor: FreeTrialManager shows inactive trial or no remaining prompts, keeping settings open");
          }
        } catch (err) {
          console.error("TrialStatusMonitor: Error checking trial status:", err);
        }
      }
    };

    // Listen for trial activation events
    const handleTrialActivated = () => {
      console.log("TrialStatusMonitor: Trial became active, checking if settings is open for API key required");
      
      // Only close settings if it was opened for API key required
      if (apiKeyDialogOpen) {
        // Also check if the trial has remaining prompts
        const FreeTrialManager = require('./components/FreeTrialManager').default;
        if (FreeTrialManager.getRemainingPromptCount() > 0) {
          console.log("TrialStatusMonitor: Closing settings because it was opened for API key required and trial now has prompts");
          if (onCloseSettings) {
            onCloseSettings();
            setApiKeyDialogOpen(false);
          }
        } else {
          console.log("TrialStatusMonitor: Keeping settings open despite trial activation because no prompts remain");
        }
      }
    };

    // Add event listeners
    window.addEventListener('openSettings', handleSettingsOpened);
    window.addEventListener('trialUpdated', handleTrialActivated);
    
    // Return cleanup function
    return () => {
      window.removeEventListener('openSettings', handleSettingsOpened);
      window.removeEventListener('trialUpdated', handleTrialActivated);
    };
  }, [onCloseSettings]);

  // Component doesn't render anything visible
  return null;
}

// For backup plain-text key handling if encryption fails
const getApiKey = () => {
  console.log("App: getApiKey called, checking for trial first");
  
  // Check for free trial
  try {
    const FreeTrialManager = require('./components/FreeTrialManager').default;
    if (FreeTrialManager.isTrialActive()) {
      const trialKey = FreeTrialManager.getTrialApiKey();
      console.log("App: Free trial is active, using trial API key");
      if (trialKey) return trialKey;
    } else {
      // If not active but stored data exists, try reinitializing
      const storedTrial = localStorage.getItem('freeTrialData');
      if (storedTrial) {
        try {
          const parsedData = JSON.parse(storedTrial);
          // If has remaining prompts but not active, try to reactivate
          if (parsedData.remaining > 0 && !parsedData.active) {
            console.log("App: Trial has remaining prompts but inactive, trying to reactivate");
            parsedData.active = true;
            localStorage.setItem('freeTrialData', JSON.stringify(parsedData));
            
            // Check again after fixing
            if (FreeTrialManager.isTrialActive()) {
              const key = FreeTrialManager.getTrialApiKey();
              if (key) {
                console.log("App: Successfully reactivated trial");
                return key;
              }
            }
          }
        } catch (err) {
          console.error("App: Error parsing trial data:", err);
        }
      }
    }
  } catch (err) {
    console.error("App: Error checking for trial key:", err);
  }
  
  console.log("App: No trial key, checking for user API key");
  // Continue with normal key handling
  const encryptedKey = localStorage.getItem('anthropic_api_key_encrypted');
  const plainKey = localStorage.getItem('anthropic_api_key');
  
  if (encryptedKey) {
    try {
      const decrypted = decryptData(encryptedKey);
      if (decrypted && decrypted.trim() !== '') {
        return decrypted;
      }
    } catch (err) {
      console.error("Error decrypting key:", err);
      // Fall back to plain key if available
    }
  }
  
  return plainKey || '';
};

// Generate thread title using AI
const generateThreadName = async (messageContent) => {
  if (!messageContent || messageContent.trim() === '') {
    return "New Topic";
  }
  
  try {
    // Get the API key
    const apiKey = getApiKey();
    
    if (!apiKey || apiKey.trim() === '') {
      console.log("No API key available for thread naming");
      return messageContent.length > 30 ? messageContent.substring(0, 30) + "..." : messageContent;
    }
    
    // Create a temporary client
    const client = new Anthropic({
      apiKey: apiKey,
      dangerouslyAllowBrowser: true,
    });
    
    // Call the API to generate a thread name
    const response = await client.messages.create({
      model: "claude-3-7-sonnet-20250219", // Always use Claude 3.7 Sonnet for naming
      max_tokens: 50,
      messages: [{
        role: "user",
        content: `Please create a concise, descriptive title for a conversation thread that begins with this message. 
        The title should be 2-6 words and capture the essence of what might be discussed.
        Return ONLY the title, with no quotes or explanations.
        
        Message: "${messageContent}"`
      }],
      temperature: 0.7
    });
    
    // Track API call
    apiUsageTracker.trackApiCall(response);
    
    let threadName = "New Topic";
    if (response?.content && response.content[0]?.text) {
      // Clean up the response
      threadName = response.content[0].text.trim();
      
      // Remove any quotation marks
      threadName = threadName.replace(/^["']|["']$/g, '');
      
      // Limit length
      if (threadName.length > 40) {
        threadName = threadName.substring(0, 40) + "...";
      }
    }
    
    return threadName;
  } catch (error) {
    console.error("Failed to generate thread name:", error);
    // Track API error
    apiUsageTracker.trackApiError(error);
    // Fall back to a substring of the message
    return messageContent.length > 30 ? messageContent.substring(0, 30) + "..." : messageContent;
  }
};

// Simplified trial initialization without warnings
const initializeFreeTrial = async () => {
  try {
    const FreeTrialManager = require('./components/FreeTrialManager').default;
    console.log("App: Initializing free trial");
    const trialActive = await FreeTrialManager.initTrial();
    
    if (trialActive) {
      console.log("App: Free trial is active with", FreeTrialManager.getRemainingPromptCount(), "prompts remaining");
    }
    
    return trialActive;
  } catch (err) {
    console.error("App: Error initializing free trial:", err);
    return false;
  }
};

function App() {
  const [topics, setTopics] = useState([]);
  const [activeTopic, setActiveTopic] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [anthropicClient, setAnthropicClient] = useState(null);
  const [selectedModel, setSelectedModel] = useState('claude-3-7-sonnet-20250219');
  const [contextLevel, setContextLevel] = useState('maximum');
  const [isTopicsLoading, setIsTopicsLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [contextUtilization, setContextUtilization] = useState(0);
  const [isClientInitializing, setIsClientInitializing] = useState(false);
  const [appReady, setAppReady] = useState(false);
  const [showResourceWarning, setShowResourceWarning] = useState(false);
  const [resourceUsage, setResourceUsage] = useState(0);
  const [enableResourceWarnings, setEnableResourceWarnings] = useState(true);
  const messageInputRef = useRef(''); // Added for saving message input
  
  // Auto-continue state
  const [continuationInProgress, setContinuationInProgress] = useState(false);
  const [continuationCount, setContinuationCount] = useState(0);
  
  // Reset the showSettings state when settings dialog should be shown explicitly
  const openSettings = useCallback(() => {
    console.log("App: Opening settings dialog through callback");
    setShowSettings(true);
  }, []);
  
  // Function to close settings - used by TrialStatusMonitor
  const closeSettings = useCallback(() => {
    console.log("App: Closing settings dialog through callback");
    setShowSettings(false);
  }, []);
  
  // Listen for 'openSettings' events (e.g., from API key required modal)
  useEffect(() => {
    const handleOpenSettings = (event) => {
      console.log("App: Opening settings from event", event.detail?.reason);
      setShowSettings(true);
      
      // Optionally focus the API key field
      setTimeout(() => {
        const apiKeyInput = document.getElementById('api-key-input');
        if (apiKeyInput) {
          apiKeyInput.focus();
        }
      }, 100);
    };
    
    // Also listen for closeSettings events (can be triggered by TrialStatusMonitor)
    const handleCloseSettings = (event) => {
      console.log("App: Closing settings from event", event.detail?.reason);
      setShowSettings(false);
    };
    
    window.addEventListener('openSettings', handleOpenSettings);
    window.addEventListener('closeSettings', handleCloseSettings);
    
    return () => {
      window.removeEventListener('openSettings', handleOpenSettings);
      window.removeEventListener('closeSettings', handleCloseSettings);
    };
  }, []);
  
  // Check if trial is expired and no API key during initialization
  useEffect(() => {
    // Add a delay before checking trial status to ensure initialization has completed
    const timer = setTimeout(() => {
      const checkTrialStatus = async () => {
        try {
          const FreeTrialManager = require('./components/FreeTrialManager').default;
          
          // If trial is not active AND we don't have a user API key
          if (!FreeTrialManager.isTrialActive()) {
            const apiKey = getApiKey();
            
            // If we have no API key, show settings
            if (!apiKey || apiKey.trim() === '') {
              console.log("App: No active trial and no API key, should show settings");
              // Only show if we're still in initial app state
              if (initialLoad) {
                setShowSettings(true);
              }
            }
          }
        } catch (err) {
          console.error("App: Error checking trial status:", err);
        }
      };
      
      checkTrialStatus();
    }, 1500); // Add 1.5 second delay to allow trial initialization to complete
    
    return () => clearTimeout(timer);
  }, [initialLoad]);
  
  // Check API usage limit periodically
  useEffect(() => {
    const checkApiUsage = () => {
      const usagePercentage = apiUsageTracker.getRateLimitPercentage();
      setResourceUsage(usagePercentage);
      
      // Only show warning if warnings are enabled and we're approaching rate limit
      if (enableResourceWarnings && apiUsageTracker.isApproachingRateLimit()) {
        setShowResourceWarning(true);
      }
      
      // If rate limited, always show error even if warnings are disabled
      if (apiUsageTracker.isRateLimited()) {
        const timeUntilReset = apiUsageTracker.getTimeUntilReset();
        const minutesRemaining = Math.ceil(timeUntilReset / 60000);
        setError(`API rate limit reached. Please wait approximately ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''} before sending more messages.`);
        
        // Set timer to clear error after 5 seconds
        setTimeout(() => {
          const fadeOutError = () => {
            // First add a fade-out class
            const errorElement = document.querySelector('.error-message');
            if (errorElement) {
              errorElement.classList.add('fade-out');
            }
            
            // Then after the animation is complete, remove the error
            setTimeout(() => {
              setError(null);
            }, 300); // Animation duration in ms
          };
          
          fadeOutError();
        }, 4700); // 5000ms - 300ms animation duration
      }
    };
    // Initial check
    checkApiUsage();
    
    // Set up interval
    const interval = setInterval(checkApiUsage, 60000); // Check every minute
    
    return () => clearInterval(interval);
  }, [enableResourceWarnings]);
  
  // Initialize Anthropic client
  const initializeClient = useCallback(async () => {
    console.log("Initializing client...");
    
    // Get stored settings
    const apiKey = getApiKey();
    const storedModel = localStorage.getItem('anthropic_model') || 'claude-3-7-sonnet-20250219';
    const storedContextLevel = localStorage.getItem('conversation_context_level') || 'maximum';
    const storedResourceWarnings = localStorage.getItem('show_resource_warnings');
    const showWarnings = storedResourceWarnings !== null ? storedResourceWarnings === 'true' : true;
    
    console.log("Using stored model:", storedModel);
    
    // Set model and context level regardless of key status
    setSelectedModel(storedModel);
    setContextLevel(storedContextLevel);
    setEnableResourceWarnings(showWarnings);
    
    let hasValidKey = false;
    
    if (apiKey && apiKey.trim() !== '') {
      console.log("Found stored API key, attempting to create client");
      hasValidKey = true;
      setIsClientInitializing(true);
      
      try {
        const client = new Anthropic({
          apiKey: apiKey,
          dangerouslyAllowBrowser: true,
        });
        
        // Short delay to ensure client is properly initialized
        await new Promise(resolve => setTimeout(resolve, 100));
        
        setAnthropicClient(client);
        setError(null);
        
        // Log client creation
        console.log("Client successfully created with model:", storedModel);
      } catch (err) {
        console.error("Error creating Anthropic client:", err);
        hasValidKey = false; // Client creation failed
      } finally {
        setIsClientInitializing(false);
      }
    }
    
    // Only show settings on first load if we don't have a valid key
    console.log("Has valid key:", hasValidKey, "Initial load:", initialLoad);
    if (!hasValidKey && initialLoad) {
      // Check if free trial is available and initialize it if needed
      try {
        const FreeTrialManager = require('./components/FreeTrialManager').default;
        console.log("App: Initializing free trial before showing settings");
        const trialActive = await FreeTrialManager.initTrial();
        
        if (trialActive) {
          console.log("App: Free trial initialized successfully, not showing settings");
          
          // Create client with trial key
          const trialKey = FreeTrialManager.getTrialApiKey();
          if (trialKey) {
            try {
              const client = new Anthropic({
                apiKey: trialKey,
                dangerouslyAllowBrowser: true,
              });
              
              setAnthropicClient(client);
              hasValidKey = true; // We now have a valid key via trial
              console.log("App: Created client with trial API key");
            } catch (err) {
              console.error("App: Failed to create client with trial key:", err);
            }
          }
        } else {
          console.log("App: No active trial, showing settings");
          setShowSettings(true);
        }
      } catch (err) {
        console.error("App: Error initializing free trial:", err);
        console.log("App: No valid key on initial load, showing settings");
        setShowSettings(true);
      }
    }
    
    return hasValidKey;
  }, [initialLoad]);
  
  // Don't initialize app again if settings are explicitly opened
  useEffect(() => {
    let isMounted = true;
    
    // Skip initialization if this is just a settings toggle
    if (showSettings && !initialLoad) {
      console.log("App: Skipping initialization because settings were explicitly opened");
      return;
    }
    
    const initializeApp = async () => {
      console.log("Starting app initialization");
      if (!isMounted) return;
      
      setAppReady(false);
      
      // New approach: Initialize free trial first, with higher priority
      let trialInitialized = false;
      try {
        console.log("App: Initializing free trial with high priority");
        const FreeTrialManager = require('./components/FreeTrialManager').default;
        
        // Try up to 2 times to initialize the trial if needed
        trialInitialized = await FreeTrialManager.initTrial();
        
        if (!trialInitialized) {
          console.log("App: First trial initialization failed, trying again...");
          // Short delay before retry
          await new Promise(resolve => setTimeout(resolve, 100)); 
          trialInitialized = await FreeTrialManager.initTrial(true); // Force new on second attempt
        }
        
        console.log("App: Free trial initialization result:", trialInitialized);
        
        // If trial is active, create a client with the trial key
        if (trialInitialized) {
          const trialKey = FreeTrialManager.getTrialApiKey();
          if (trialKey) {
            try {
              console.log("App: Creating Anthropic client with trial key");
              const client = new Anthropic({
                apiKey: trialKey,
                dangerouslyAllowBrowser: true,
              });
              
              setAnthropicClient(client);
              console.log("App: Successfully created client with trial key");
              
              // Dispatch trial updated event to notify other components
              window.dispatchEvent(new Event('trialUpdated'));
            } catch (err) {
              console.error("App: Failed to create client with trial key:", err);
              trialInitialized = false;
            }
          } else {
            console.log("App: Trial active but no key available");
            trialInitialized = false;
          }
        }
      } catch (err) {
        console.error("App: Error during free trial initialization:", err);
        trialInitialized = false;
      }
      
      // If trial didn't initialize, try with API key
      if (!trialInitialized) {
        console.log("App: Trial not initialized, checking for API key");
        const hasValidKey = await initializeClient();
        
        if (!hasValidKey && initialLoad) {
          // Show settings if no valid key and no trial
          console.log("App: No valid key or trial on initial load, showing settings");
          setShowSettings(true);
        }
      }
      
      if (!isMounted) return;
      
      // Initialize prompt library from CSV
      try {
        const promptsInitialized = await initializeFromCsv();
        if (promptsInitialized && isMounted) {
          console.log('Prompt library initialized with default prompts');
        }
      } catch (error) {
        console.error('Error initializing prompt library:', error);
      }
      
      // Load topics using IndexedDB
      console.log("Loading topics from IndexedDB...");
      setIsTopicsLoading(true);
      
      try {
        // Get all threads from IndexedDB
        const threads = await threadService.getAllThreads();
        console.log(`Loaded ${threads.length} threads from IndexedDB`);
        
        if (isMounted) {
          setTopics(threads);
          
          if (threads.length > 0) {
            setActiveTopic(threads[0].id);
          }
        }
      } catch (e) {
        console.error("Error loading threads from IndexedDB:", e);
        if (isMounted) {
          setTopics([]);
        }
      }
      
      if (isMounted) {
        setIsTopicsLoading(false);
        setInitialLoad(false);
        setAppReady(true);
        console.log("App initialization complete");
      }
    };
    
    initializeApp();
    
    return () => {
      isMounted = false;
    };
  }, [initializeClient, showSettings, initialLoad]);
  
  // Update context utilization when active topic or context level changes
  useEffect(() => {
    const updateContextUtilization = async () => {
      if (activeTopic) {
        try {
          // Get the complete thread with messages from IndexedDB
          const currentTopic = await threadService.getThread(activeTopic);
          
          if (currentTopic && currentTopic.messages && currentTopic.messages.length > 0) {
            const percentage = getContextUtilizationPercentage(
              currentTopic.messages, 
              selectedModel, 
              contextLevel
            );
            setContextUtilization(percentage);
          }
        } catch (error) {
          console.error("Error getting thread for context utilization:", error);
        }
      }
    };
    
    updateContextUtilization();
  }, [activeTopic, contextLevel, selectedModel]);

  const createNewTopic = async () => {
    try {
      // Check if there's already an empty topic
      const emptyTopic = topics.find(topic => 
        (!topic.messages || topic.messages.length === 0) && 
        topic.title === "New Topic"
      );
      
      // If there's already an empty topic, select it instead of creating a new one
      if (emptyTopic) {
        setActiveTopic(emptyTopic.id);
        setError(null);
        
        // Track thread opening in IndexedDB
        await threadService.trackThreadOpening(emptyTopic.id);
        
        // Use requestAnimationFrame for DOM updates
        requestAnimationFrame(() => {
          document.querySelectorAll('.topic-item').forEach(item => {
            item.classList.remove('selected');
          });
          const newElement = document.querySelector(`.topic-item[data-id="${emptyTopic.id}"]`);
          if (newElement) {
            newElement.classList.add('selected');
          }
        });
        
        return;
      }
      
      // Create new topic with IndexedDB
      const newTopicId = await threadService.createThread("New Topic", selectedModel);
      console.log("Created new topic with ID:", newTopicId);
      
      // Get the thread to get the complete object
      const newThread = {
        id: newTopicId,
        title: "New Topic",
        messages: [],
        lastUpdated: Date.now(),
        createdAt: Date.now(),
        model: selectedModel
      };
      
      // Update state
      setTopics(prevTopics => [newThread, ...(prevTopics || [])]);
      setActiveTopic(newTopicId);
      setError(null);
      
      // Use requestAnimationFrame for DOM updates
      requestAnimationFrame(() => {
        document.querySelectorAll('.topic-item').forEach(item => {
          item.classList.remove('selected');
        });
        const newElement = document.querySelector(`.topic-item[data-id="${newTopicId}"]`);
        if (newElement) {
          newElement.classList.add('selected');
        }
      });
    } catch (error) {
      console.error("Error creating new topic:", error);
      setError("Failed to create new topic: " + error.message);
    }
  };

  const handleDeleteTopic = async (topicId) => {
    try {
      // First, update UI immediately for responsiveness
      setTopics(prevTopics => prevTopics.filter(topic => topic.id !== topicId));
      
      // Handle UI updates for active topic if needed
      if (activeTopic === topicId) {
        // If there are other topics, set the first one as active
        if (topics.length > 1) {
          const remainingTopics = topics.filter(topic => topic.id !== topicId);
          setActiveTopic(remainingTopics[0].id);
          
          // Use requestAnimationFrame for DOM updates
          requestAnimationFrame(() => {
            const newSelectedElement = document.querySelector(`.topic-item[data-id="${remainingTopics[0].id}"]`);
            if (newSelectedElement) {
              newSelectedElement.classList.add('selected');
            }
          });
        } else {
          setActiveTopic(null);
        }
      }
      
      // Now, perform the actual deletion in the database
      await threadService.deleteThread(topicId);
      
      // Cleanup file references asynchronously 
      cleanupThreadFileReferences(topicId)
        .then((result) => {
          console.log(`File reference cleanup for thread ${topicId} complete:`, result);
          
          // Optional: Show notification if significant cleanup happened
          if (result.deleted > 0) {
            console.log(`Cleaned up ${result.deleted} orphaned file references`);
          }
        })
        .catch((err) => {
          // Just log errors, don't block the UI
          console.error(`Error during file reference cleanup for thread ${topicId}:`, err);
        });
    } catch (error) {
      console.error("Error deleting topic:", error);
      setError("Failed to delete topic: " + error.message);
    }
  };

  const handleRenameTopic = async (topicId, newTitle) => {
    try {
      // Update UI immediately
      setTopics(prevTopics =>
        prevTopics.map(topic =>
          topic.id === topicId
            ? { ...topic, title: newTitle }
            : topic
        )
      );
      
      // Update in database
      await threadService.renameThread(topicId, newTitle);
    } catch (error) {
      console.error("Error renaming topic:", error);
      setError("Failed to rename topic: " + error.message);
    }
  };

  const handleSettingsSave = (settings) => {
    console.log("Saving settings:", settings);
    setError(null); // Clear error message immediately
    
    const modelChanged = settings.model !== selectedModel;
    
    if (settings.apiKey) {
      // Update Anthropic client with new API key
      try {
        const client = new Anthropic({
          apiKey: settings.apiKey,
          dangerouslyAllowBrowser: true,
        });
        setAnthropicClient(client);
      } catch (err) {
        console.error("Error creating Anthropic client:", err);
        setError("Failed to initialize Anthropic client with the provided API key");
      }
    }
    
    // Update model and context level
    setSelectedModel(settings.model || 'claude-3-7-sonnet-20250219');
    setContextLevel(settings.contextLevel || 'maximum');
    
    // Update resource warnings setting
    if (settings.showResourceWarnings !== undefined) {
      setEnableResourceWarnings(settings.showResourceWarnings);
      
      // If warnings are disabled, also hide any current warning
      if (!settings.showResourceWarnings) {
        setShowResourceWarning(false);
      }
    }
    
    // Log the model change
    if (modelChanged) {
      console.log("Model changed from", selectedModel, "to", settings.model);
    }
    
    // Dispatch event to notify that settings were saved
    window.dispatchEvent(new CustomEvent('settingsDialogClosed', { 
      detail: { settingsShown: true } 
    }));
  };

  // Utility function to check if a response appears to be truncated
  const isResponseTruncated = (response) => {
    // Check if response is near the token limit
    const isNearTokenLimit = response.usage && 
      response.usage.output_tokens >= (4096 - 100); // Within 100 tokens of limit
    
    if (!isNearTokenLimit) return false;
    
    // Check content for abrupt endings
    const content = response.content[0].text;
    
    // Check for code blocks without closing backticks
    const codeBlocksOpened = (content.match(/```[a-z]*/g) || []).length;
    const codeBlocksClosed = (content.match(/```\s*$/gm) || []).length;
    
    // Check for incomplete sentences (no ending punctuation at the end)
    const endsWithPunctuation = /[.!?;]\s*$/.test(content);
    
    // Check for natural conclusion markers
    const hasConclusion = /in conclusion|to summarize|in summary|finally,/i.test(
      content.slice(-150)
    );
    
    return (
      (codeBlocksOpened > codeBlocksClosed) || 
      (!endsWithPunctuation && !hasConclusion)
    );
  };
  
  // Function to handle auto-continuation with IndexedDB
  const handleContinuation = async (topicId, previousMessages) => {
    console.log(`Starting continuation ${continuationCount + 1} of ${MAX_CONTINUATIONS}`);
    
    if (continuationCount >= MAX_CONTINUATIONS) {
      try {
        // Get the thread to find the last message
        const thread = await threadService.getThread(topicId);
        if (!thread || !thread.messages || thread.messages.length === 0) {
          throw new Error("Thread not found or empty");
        }
        
        // Get the last message (assistant message)
        const lastMessageIndex = thread.messages.length - 1;
        const lastMessage = thread.messages[lastMessageIndex];
        
        // Update the message with a note
        const updatedContent = lastMessage.content + 
          "\n\n[Maximum response length reached. Ask a follow-up question for more information.]";
        
        // Update in database
        await threadService.updateMessage(lastMessage.id, {
          content: updatedContent,
          continuationInProgress: false,
          continuationComplete: true
        });
        
        // Update in UI
        setTopics((prevTopics) =>
          prevTopics.map((topic) =>
            topic.id === topicId
              ? {
                  ...topic,
                  messages: topic.messages.map((msg, i) => 
                    i === topic.messages.length - 1 
                      ? { 
                          ...msg, 
                          content: updatedContent,
                          continuationInProgress: false,
                          continuationComplete: true
                        }
                      : msg
                  )
                }
              : topic
          )
        );
      } catch (error) {
        console.error("Error updating message for max continuations:", error);
      }
      
      setContinuationInProgress(false);
      setContinuationCount(0);
      return;
    }
    
    try {
      // Get API key for continuation
      const apiKey = getApiKey();
      
      // Create client for continuation if needed
      let client = anthropicClient;
      if (!client && apiKey) {
        client = new Anthropic({
          apiKey: apiKey,
          dangerouslyAllowBrowser: true,
        });
      }
      
      if (!client) {
        throw new Error("No API client available for continuation");
      }
      
      // Add a continue prompt
      const continuationMessages = [
        ...previousMessages,
        { role: 'user', content: 'Please continue your previous response.' }
      ];
      
      // Create a custom system message
      const modelNameMap = {
        "claude-3-7-sonnet-20250219": "Claude 3.7 Sonnet",
        "claude-3-opus-20240229": "Claude 3 Opus",
        "claude-3-sonnet-20240229": "Claude 3 Sonnet", 
        "claude-3-haiku-20241022": "Claude 3.5 Haiku",
        "claude-2.1": "Claude 2.1"
      };
      
      const modelName = modelNameMap[selectedModel] || "Claude";
      const systemMessage = `You are a helpful AI assistant named ${modelName}. Continue directly from your previous response without summarizing what you already said. Do not repeat information you've already covered. Do not say "to continue" or "as I was saying" - just continue naturally as if you never stopped typing.`;
      
      // Call Claude API for continuation
      const response = await client.messages.create({
        model: selectedModel,
        max_tokens: 4096,
        messages: continuationMessages,
        system: systemMessage,
        temperature: 0.7
      });
      
      // Track API call for rate limiting
      apiUsageTracker.trackApiCall(response);
      
      // Get the thread to find the last message
      const thread = await threadService.getThread(topicId);
      if (!thread || !thread.messages || thread.messages.length === 0) {
        throw new Error("Thread not found or empty");
      }
      
      // Get the last message (assistant message)
      const lastMessageIndex = thread.messages.length - 1;
      const lastMessage = thread.messages[lastMessageIndex];
      
      // Update the message with the continued content
      const updatedContent = lastMessage.content + "\n\n" + response.content[0].text;
      const isTruncated = isResponseTruncated(response);
      
      // Update in database
      await threadService.updateMessage(lastMessage.id, {
        content: updatedContent,
        continuationInProgress: isTruncated,
        continuationComplete: !isTruncated
      });
      
      // Update UI
      setTopics((prevTopics) =>
        prevTopics.map((topic) =>
          topic.id === topicId
            ? {
                ...topic,
                messages: topic.messages.map((msg, i) => 
                  i === topic.messages.length - 1 
                    ? { 
                        ...msg, 
                        content: updatedContent,
                        continuationInProgress: isTruncated
                      }
                    : msg
                ),
                lastUpdated: Date.now()
              }
            : topic
        )
      );
      
      // Check if we need another continuation
      if (isTruncated && continuationCount < MAX_CONTINUATIONS) {
        // Increment the continuation count
        setContinuationCount((prev) => prev + 1);
        
        // Prepare messages for next continuation
        const updatedMessages = [
          ...previousMessages,
          { role: 'user', content: 'Please continue your previous response.' },
          { role: 'assistant', content: response.content[0].text }
        ];
        
        // Continue after a short delay to allow UI to update
        setTimeout(() => {
          handleContinuation(topicId, updatedMessages);
        }, 500);
      } else {
        // Mark continuation as complete
        await threadService.updateMessage(lastMessage.id, {
          continuationInProgress: false,
          continuationComplete: true
        });
        
        setTopics((prevTopics) =>
          prevTopics.map((topic) =>
            topic.id === topicId
              ? {
                  ...topic,
                  messages: topic.messages.map((msg, i) => 
                    i === topic.messages.length - 1 
                      ? { 
                          ...msg, 
                          continuationInProgress: false,
                          continuationComplete: true
                        }
                      : msg
                  )
                }
              : topic
          )
        );
        
        setContinuationInProgress(false);
        setContinuationCount(0);
      }
    } catch (error) {
      console.error("Continuation error:", error);
      
      try {
        // Get the thread to find the last message
        const thread = await threadService.getThread(topicId);
        if (!thread || !thread.messages || thread.messages.length === 0) {
          throw new Error("Thread not found or empty");
        }
        
        // Get the last message (assistant message)
        const lastMessageIndex = thread.messages.length - 1;
        const lastMessage = thread.messages[lastMessageIndex];
        
        // Update the message with an error note
        const updatedContent = lastMessage.content + 
          "\n\n[Continuation failed. Please ask a follow-up question.]";
        
        // Update in database
        await threadService.updateMessage(lastMessage.id, {
          content: updatedContent,
          continuationInProgress: false,
          continuationComplete: true
        });
        
        // Update UI
        setTopics((prevTopics) =>
          prevTopics.map((topic) =>
            topic.id === topicId
              ? {
                  ...topic,
                  messages: topic.messages.map((msg, i) => 
                    i === topic.messages.length - 1 
                      ? { 
                          ...msg, 
                          content: updatedContent,
                          continuationInProgress: false,
                          continuationComplete: true
                        }
                      : msg
                  )
                }
              : topic
          )
        );
      } catch (err) {
        console.error("Error updating message after continuation failure:", err);
      }
      
      setContinuationInProgress(false);
      setContinuationCount(0);
    }
  };
  
  const callClaudeWithRetry = async (messages) => {
    // Check if we're rate limited
    if (apiUsageTracker.isRateLimited()) {
      const timeUntilReset = apiUsageTracker.getTimeUntilReset();
      const minutesRemaining = Math.ceil(timeUntilReset / 60000);
      throw new Error(`API rate limit reached. Please wait approximately ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''} before sending more messages.`);
    }
    
    if (!anthropicClient) {
      throw new Error("Please configure your Anthropic API key in Settings");
    }
    
    // Log the model being used for debugging
    console.log("Using model for API call:", selectedModel);
    
    // Create a custom system message that includes the model version information
    // This ensures Claude correctly identifies itself with the right version
    const modelNameMap = {
      "claude-3-7-sonnet-20250219": "Claude 3.7 Sonnet",
      "claude-3-opus-20240229": "Claude 3 Opus",
      "claude-3-sonnet-20240229": "Claude 3 Sonnet", 
      "claude-3-haiku-20241022": "Claude 3.5 Haiku",
      "claude-2.1": "Claude 2.1"
    };
    
    const modelName = modelNameMap[selectedModel] || "Claude";
    
    const systemMessage = `You are a helpful AI assistant named ${modelName}. When users upload files, examine their content carefully and assist with any questions about them. If a file upload appears to fail or you can't access the content, politely ask the user to try uploading again. For PDFs and binary files, let the user know if you're unable to process them directly. Maintain context from the conversation history to provide relevant and contextual responses. When writing code, always include appropriate language markers (e.g., \`\`\`python, \`\`\`javascript) for proper syntax highlighting and begin with a suggested filename in a comment. If asked about your version, always identify yourself as ${modelName}.`;
    
    // Create a timeout Promise that rejects after 20 seconds
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error("API_TIMEOUT"));
      }, 20000); // 20 seconds timeout
    });
    for (let i = 0; i < RETRY_DELAYS.length + 1; i++) {
      try {
        // Race between the API call and the timeout
        const response = await Promise.race([
          anthropicClient.messages.create({
            model: selectedModel,
            max_tokens: 4096,
            messages: messages,
            system: systemMessage,
            temperature: 0.7
          }),
          timeoutPromise
        ]);
        
        // Track API call for rate limiting
        apiUsageTracker.trackApiCall(response);
        
        return response;
      } catch (error) {
        // Check if this is a timeout error or busy error
        if (error.message === "API_TIMEOUT" || 
            error.message.includes("busy") || 
            error.status === 429 || 
            error.message.includes("rate limit") ||
            error.message.includes("too many requests")) {
          // Standardized message for all busy-related errors
          throw new Error("API_BUSY");
        }
        
        // Track API error for rate limiting
        apiUsageTracker.trackApiError(error);
        
        // Handle regular API errors as before
        if (error.status === 529 && i < RETRY_DELAYS.length) {
          await sleep(RETRY_DELAYS[i]);
          continue;
        }
        throw error;
      }
    }
  };
  
  const handleMessageSubmit = async (message) => {
    // Reset continuation state when user sends a new message
    setContinuationInProgress(false);
    setContinuationCount(0);
    
    // Explicitly check if this is an enhancement request
    if (message.isEnhancementRequest === true) {
      console.log("Skipping message submit for enhancement request");
      return;
    }
    if (!activeTopic) return;
    
    // If custom API key is provided, use it instead of the stored one
    const currentApiKey = message.apiKey || getApiKey();
    console.log("Using API key from:", message.apiKey ? "message parameter" : "stored settings");
    
    // Check if we're rate limited
    if (apiUsageTracker.isRateLimited()) {
      const timeUntilReset = apiUsageTracker.getTimeUntilReset();
      const minutesRemaining = Math.ceil(timeUntilReset / 60000);
      setError(`API rate limit reached. Please wait approximately ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''} before sending more messages.`);
      
      // Set timer to clear error after 5 seconds
      setTimeout(() => {
        const fadeOutError = () => {
          // First add a fade-out class
          const errorElement = document.querySelector('.error-message');
          if (errorElement) {
            errorElement.classList.add('fade-out');
          }
          
          // Then after the animation is complete, remove the error
          setTimeout(() => {
            setError(null);
          }, 300); // Animation duration in ms
        };
        
        fadeOutError();
      }, 4700); // 5000ms - 300ms animation duration
      
      return;
    }
    
    // Show warning if approaching rate limit and warnings are enabled
    if (enableResourceWarnings && apiUsageTracker.isApproachingRateLimit() && !showResourceWarning) {
      setShowResourceWarning(true);
    }
    
    // Don't allow submission if client is still initializing or not ready
    if (isClientInitializing) {
      setError("Please wait, initializing API connection...");
      
      // Set timer to clear error after 5 seconds
      setTimeout(() => {
        const fadeOutError = () => {
          // First add a fade-out class
          const errorElement = document.querySelector('.error-message');
          if (errorElement) {
            errorElement.classList.add('fade-out');
          }
          
          // Then after the animation is complete, remove the error
          setTimeout(() => {
            setError(null);
          }, 300); // Animation duration in ms
        };
        
        fadeOutError();
      }, 4700); // 5000ms - 300ms animation duration
      
      return;
    }
    
    // If client is null but we have a key, create a temporary client
    let tempClient = null;
    if (!anthropicClient && currentApiKey) {
      try {
        tempClient = new Anthropic({
          apiKey: currentApiKey,
          dangerouslyAllowBrowser: true,
        });
        console.log("Created temporary client for this request");
      } catch (err) {
        console.error("Error creating temporary client:", err);
        setError("Please configure your Anthropic API key in Settings");
        setShowSettings(true);
        return;
      }
    } else if (!anthropicClient) {
      setError("Please configure your Anthropic API key in Settings");
      setShowSettings(true);
      return;
    }
    
    // Create a proper deep copy of files to ensure we preserve ALL data
    let currentFiles = message.files ? message.files.map(file => {
      // Create a new file object with all properties
      const fileCopy = { ...file };
      
      // Ensure binary data is properly preserved
      if (file.buffer) fileCopy.buffer = file.buffer;
      if (file.dataUrl) fileCopy.dataUrl = file.dataUrl;
      if (file.content) fileCopy.content = file.content;
      
      // Make sure we have all the metadata
      if (file.text) fileCopy.text = file.text;
      if (file.html) fileCopy.html = file.html;
      if (file.parsed) fileCopy.parsed = file.parsed;
      
      return fileCopy;
    }) : [];
    
    // Create cleaned file metadata for storage
    const cleanedFiles = message.cleanedFiles || cleanFilesForStorage(currentFiles);
    
    // Set loading state for UI
    setIsLoading(true);
    setError(null);
    
    try {
      // Process all files to ensure their content is properly extracted
      let processedCurrentFiles = currentFiles;
      if (currentFiles.length > 0) {
        console.log("Processing files before submission");
        processedCurrentFiles = await processFiles(currentFiles);
        console.log("File processing complete", processedCurrentFiles);
      }
      
      // Update currentFiles to use the processed versions
      currentFiles = processedCurrentFiles;
      
      // Now continue with normal processing
      // Keep original message content for API calls
      let apiContent = message.content || "";
      
      // Use just the message content for display, not concatenated with files
      let displayContent = message.content || "";
      
      let messageInput = message.content || "";
      
      if (messageInput.length > 30) {
        messageInput = messageInput.substring(0, 30) + "...";
      }
      
      if (!messageInput && message.files && message.files.length > 0) {
        messageInput = `Question about ${message.files[0].name}`;
      }
      
      // Check if we have PDF files - used for PDF-specific API handling
      const hasPdfFiles = needsPdfBetaHeader(currentFiles || []);
      
      // Only add file content to apiContent for non-PDF files
      // For PDFs, we'll handle them separately with the PDF API
      if (currentFiles && currentFiles.length > 0) {
        apiContent += "\n\nAttached files:\n";
        currentFiles.forEach(file => {
          if (!file.type.startsWith('application/pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
            // Use the new formatter function for consistent file formatting
            apiContent += formatFileContentForClaude(file);
          } else {
            // For PDF files, just add a reference but don't include content - will be handled by PDF API
            apiContent += `\nFile: ${file.name} (PDF document, ${(file.size / 1024).toFixed(2)}KB)\n`;
            apiContent += `PDF file attached (will be processed separately)\n`;
          }
        });
      }

      if (apiContent.length > 100000) {
        setError("Message is too long. Please reduce the size of attached files.");
        setIsLoading(false);
        
        // Save the message input so it's not lost
        messageInputRef.current = message.content;
        
        // Set timer to clear error after 5 seconds
        setTimeout(() => {
          const fadeOutError = () => {
            // First add a fade-out class
            const errorElement = document.querySelector('.error-message');
            if (errorElement) {
              errorElement.classList.add('fade-out');
            }
            
            // Then after the animation is complete, remove the error
            setTimeout(() => {
              setError(null);
            }, 300); // Animation duration in ms
          };
          
          fadeOutError();
        }, 4700); // 5000ms - 300ms animation duration
        
        return;
      }
      
      try {
        // Get current topic
        const currentTopic = await threadService.getThread(activeTopic);
        
        // Create user message object
        const userMessageObj = {
          role: 'user',
          content: displayContent,
          apiContent: apiContent,
          files: cleanedFiles || message.files
        };
        
        // Add the user message to the database
        const userMessageId = await threadService.addUserMessage(activeTopic, userMessageObj);
        console.log("Added user message with ID:", userMessageId);
        
        // Check if this is the first message (new topic) - use AI to generate name
        if (!currentTopic?.messages || currentTopic.messages.length === 0) {
          // Create temporary title from message content
          const tempTitle = message.content && message.content.length > 30 ? 
            message.content.substring(0, 30) + "..." : 
            message.content || "New Topic";
          
          // Update with temporary title first
          await threadService.renameThread(activeTopic, tempTitle);
          
          // Update UI
          setTopics(prevTopics => 
            prevTopics.map(t => 
              t.id === activeTopic ? 
                { 
                  ...t, 
                  title: tempTitle,
                  // Keep the original creation date - don't override it
                  createdAt: t.createdAt 
                } : t
            )
          );
          
          // Generate better title asynchronously
          try {
            const generatedTitle = await generateThreadName(message.content);
            if (generatedTitle !== "New Topic") {
              await threadService.renameThread(activeTopic, generatedTitle);
              
              // Update UI with generated title
              setTopics(prevTopics => 
                prevTopics.map(t => 
                  t.id === activeTopic ? { ...t, title: generatedTitle } : t
                )
              );
            }
          } catch (err) {
            console.error("Failed to generate thread name:", err);
          }
        }

        // Add user message to UI state immediately for responsiveness
        setTopics(prevTopics => 
          prevTopics.map(t => 
            t.id === activeTopic ? 
              { 
                ...t, 
                messages: [...(t.messages || []), userMessageObj],
                lastUpdated: Date.now()
              } : t
          )
        );

        // Fetch the complete thread with all messages to prepare for API call
        const fullThread = await threadService.getThread(activeTopic);
        
        // Prepare messages for API call
        const allApiMessages = fullThread.messages.map(msg => ({
          role: msg.role === 'user' ? 'user' : 'assistant',
          content: msg.apiContent || msg.content
        }));
        
        // Apply context selection rules
        const selectedApiMessages = selectMessagesForContext(
          allApiMessages, 
          selectedModel, 
          contextLevel
        );

        // Check context utilization
        const percentage = getContextUtilizationPercentage(
          fullThread.messages, 
          selectedModel, 
          contextLevel
        );
        setContextUtilization(percentage);

        try {
          // Create model name mapping for system message
          const modelNameMap = {
            "claude-3-7-sonnet-20250219": "Claude 3.7 Sonnet",
            "claude-3-opus-20240229": "Claude 3 Opus",
            "claude-3-sonnet-20240229": "Claude 3 Sonnet", 
            "claude-3-haiku-20241022": "Claude 3.5 Haiku",
            "claude-2.1": "Claude 2.1"
          };
          
          const modelName = modelNameMap[selectedModel] || "Claude";
          
          const systemMessage = `You are a helpful AI assistant named ${modelName}. When users upload files, examine their content carefully and assist with any questions about them. If a file upload appears to fail or you can't access the content, politely ask the user to try uploading again. For PDFs and binary files, let the user know if you're unable to process them directly. Maintain context from the conversation history to provide relevant and contextual responses. When writing code, always include appropriate language markers (e.g., \`\`\`python, \`\`\`javascript) for proper syntax highlighting and begin with a suggested filename in a comment. If asked about your version, always identify yourself as ${modelName}.`;
          
          let response;
          
          // Handle PDF files with the PDF-specific API format if applicable
          if (hasPdfFiles) {
            console.log("PDF files detected, using PDF API format");
            
            // Choose PDF-compatible model
            const pdfModel = getPdfSupportedModel();
            console.log(`Using PDF-compatible model: ${pdfModel}`);
            
            // Create client with PDF beta header
            const pdfClient = new Anthropic({
              apiKey: currentApiKey,
              dangerouslyAllowBrowser: true,
              defaultHeaders: {
                "anthropic-beta": "pdfs-2024-09-25"
              }
            });
            
            // Prepare PDF content blocks
            const fileContentBlocks = await prepareFilesForAnthropicApi(currentFiles || []);
            console.log(`Prepared ${fileContentBlocks.length} PDF content blocks`);
            
            // Add text content if present
            let messageContent = [...fileContentBlocks];
            if (message.content && message.content.trim()) {
              messageContent.push({
                type: "text",
                text: message.content
              });
            }
            
            // Call the API with PDF format
            response = await pdfClient.messages.create({
              model: pdfModel,
              max_tokens: 4096,
              messages: [
                {
                  role: "user",
                  content: messageContent
                }
              ],
              system: systemMessage,
              temperature: 0.7
            });
            
            console.log("Successfully received response from PDF API");
          } else {
            // Use normal API call format for non-PDF content
            console.log("Using standard API format (no PDFs detected)");
            
            // Use temporary client if created or fall back to anthropicClient
            const client = tempClient || anthropicClient;
            if (!client) throw new Error("No API client available");
            
            // Create API call with same settings as callClaudeWithRetry
            response = await client.messages.create({
              model: selectedModel,
              max_tokens: 4096, 
              messages: selectedApiMessages,
              system: systemMessage,
              temperature: 0.7
            });
          }

          // Track API call for rate limiting
          apiUsageTracker.trackApiCall(response);
          
          // Create assistant message
          const assistantMessage = {
            role: "assistant", 
            content: response.content[0].text,
            continuationInProgress: isResponseTruncated(response),
            continuationComplete: !isResponseTruncated(response)
          };
          
          // Add assistant message to database
          const assistantMessageId = await threadService.addAssistantMessage(
            activeTopic, 
            assistantMessage.content, 
            {
              continuationInProgress: assistantMessage.continuationInProgress,
              continuationComplete: assistantMessage.continuationComplete
            }
          );
          console.log("Added assistant message with ID:", assistantMessageId);
          
          // Update UI
          setTopics((prevTopics) =>
            prevTopics.map((topic) =>
              topic.id === activeTopic
                ? {
                    ...topic,
                    messages: [
                      ...(topic.messages || []),
                      assistantMessage
                    ],
                    lastUpdated: Date.now()
                  }
                : topic
            )
          );
          
          // Check if we need to initiate continuation
          if (isResponseTruncated(response)) {
            console.log("Response appears to be truncated, initiating continuation");
            setContinuationInProgress(true);
            setContinuationCount(1); // First continuation
            
            // Prepare messages for continuation
            const messagesForContinuation = [
              ...selectedApiMessages,
              { role: 'assistant', content: response.content[0].text }
            ];
            
            // Start continuation after a short delay
            setTimeout(() => {
              handleContinuation(activeTopic, messagesForContinuation);
            }, 500);
          }
          
          setError(null);

          // Clean up any other empty topics now that this one has content
          if (!message.isEnhancementRequest) {
            try {
              // Get all threads from database
              const allThreads = await threadService.getAllThreads();
              
              // Identify empty threads
              const emptyThreadIds = [];
              for (const thread of allThreads) {
                if (thread.id !== activeTopic) {
                  const fullThread = await threadService.getThread(thread.id);
                  if (!fullThread.messages || fullThread.messages.length === 0) {
                    emptyThreadIds.push(thread.id);
                  }
                }
              }
              
              // Delete empty threads
              for (const emptyId of emptyThreadIds) {
                await threadService.deleteThread(emptyId);
              }
              
              // Update UI state to remove deleted threads
              if (emptyThreadIds.length > 0) {
                setTopics(prevTopics => 
                  prevTopics.filter(topic => 
                    !emptyThreadIds.includes(topic.id)
                  )
                );
              }
            } catch (err) {
              console.error("Error cleaning up empty threads:", err);
            }
          }
        } catch (error) {
          console.error("API Error:", error);
          
          // Standardized error handling for busy/overload conditions
          const isBusyOrOverload = error.message === "API_TIMEOUT" || 
                                error.message === "API_BUSY" ||
                                error.status === 403 || 
                                error.status === 429 || 
                                error.message?.includes("busy") ||
                                error.message?.includes("overloaded") ||
                                error.message?.includes("rate limit") ||
                                error.message?.includes("too many requests");
          
          if (isBusyOrOverload) {
            // Standardized error message for busy/overload conditions
            setError("The AI service is currently busy. Please try again in a few moments.");
            
            // Save the message input to restore it in the textarea
            messageInputRef.current = message.content;
            
            // Set timer to clear error after 5 seconds
            setTimeout(() => {
              const fadeOutError = () => {
                // First add a fade-out class
                const errorElement = document.querySelector('.error-message');
                if (errorElement) {
                  errorElement.classList.add('fade-out');
                }
                
                // Then after the animation is complete, remove the error
                setTimeout(() => {
                  setError(null);
                }, 300); // Animation duration in ms
              };
              
              fadeOutError();
            }, 4700); // 5000ms - 300ms animation duration
            
            // Remove the user's message from database and UI
            try {
              // Get the message ID of the most recent message
              const thread = await threadService.getThread(activeTopic);
              const messages = thread.messages || [];
              
              // If there are messages, delete the last one (the user's message)
              if (messages.length > 0) {
                const lastMessageId = messages[messages.length - 1].id;
                
                // Delete the message from database - we need to add a deleteMessage function
                // For now, we can just update UI state
              }
              
              // Update UI state
              setTopics((prevTopics) =>
                prevTopics.map((topic) =>
                  topic.id === activeTopic
                    ? {
                        ...topic,
                        messages: topic.messages ? topic.messages.slice(0, -1) : [],
                        lastUpdated: Date.now()
                      }
                    : topic
                )
              );
            } catch (err) {
              console.error("Error removing failed message:", err);
            }
          } else {
            setError(error.message || "An error occurred while communicating with Claude");
            
            // Set timer to clear error after 5 seconds
            setTimeout(() => {
              const fadeOutError = () => {
                // First add a fade-out class
                const errorElement = document.querySelector('.error-message');
                if (errorElement) {
                  errorElement.classList.add('fade-out');
                }
                
                // Then after the animation is complete, remove the error
                setTimeout(() => {
                  setError(null);
                }, 300); // Animation duration in ms
              };
              
              fadeOutError();
            }, 4700); // 5000ms - 300ms animation duration
            
            // Remove the user's message from state only
            setTopics((prevTopics) =>
              prevTopics.map((topic) =>
                topic.id === activeTopic
                  ? {
                      ...topic,
                      messages: topic.messages ? topic.messages.slice(0, -1) : [],
                      lastUpdated: Date.now()
                    }
                  : topic
              )
            );
          }
        } finally {
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Error updating topics:", error);
        setError(error.message || "An error occurred while processing your message");
        
        // Set timer to clear error after 5 seconds
        setTimeout(() => {
          const fadeOutError = () => {
            // First add a fade-out class
            const errorElement = document.querySelector('.error-message');
            if (errorElement) {
              errorElement.classList.add('fade-out');
            }
            
            // Then after the animation is complete, remove the error
            setTimeout(() => {
              setError(null);
            }, 300); // Animation duration in ms
          };
          
          fadeOutError();
        }, 4700); // 5000ms - 300ms animation duration
        
        setIsLoading(false);
      }
    } catch (err) {
      console.error("Error processing files:", err);
      setError("Error processing files. Please try again.");
      
      // Restore the message input since the request failed
      messageInputRef.current = message.content;
      
      // Set timer to clear error
      setTimeout(() => {
        const fadeOutError = () => {
          const errorElement = document.querySelector('.error-message');
          if (errorElement) {
            errorElement.classList.add('fade-out');
          }
          setTimeout(() => {
            setError(null);
          }, 300);
        };
        fadeOutError();
      }, 4700);
      
      setIsLoading(false);
    }
  };

  // Check if context warning should be shown
  const showContextWarning = activeTopic && topics && topics.length > 0 && 
    isApproachingContextLimit(
      topics.find(t => t.id === activeTopic)?.messages || [], 
      selectedModel, 
      contextLevel
    );

  // Don't render until app is fully initialized
  if (!appReady && initialLoad) {
    return (
      <div className="app-loading" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: '#f8f9fc'
      }}>
        <Spinner size={40} />
        <h3 style={{ 
          marginTop: '24px', 
          marginBottom: '8px', 
          color: '#2d3748',
          fontWeight: '600', 
          fontSize: '20px'
        }}>
          Initializing Blue Comet
        </h3>
        <p style={{ 
          margin: '0', 
          color: '#718096', 
          fontSize: '14px',
          maxWidth: '300px',
          textAlign: 'center'
        }}>
          Your personal Claude AI chat assistant
        </p>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* Add the TrialStatusMonitor component at the top level */}
      <TrialStatusMonitor onCloseSettings={closeSettings} />
      
      <div className="sidebar">
        <button 
          onClick={createNewTopic}
          className="new-topic-button"
        >
          New Topic
        </button>
        {!isTopicsLoading && topics && (
          <TopicList
            topics={topics}
            activeTopic={activeTopic}
            setActiveTopic={setActiveTopic}
            searchTerm={searchTerm}
            onDeleteTopic={handleDeleteTopic}
            onRenameTopic={handleRenameTopic}
          />
        )}
        <div className="sidebar-footer">
          <SearchBar setSearchTerm={setSearchTerm} />
          <div className="app-branding">
            <span className="app-name">Blue Comet</span>
            <span className="app-version">Version 1.0</span>
            <button 
              className="settings-icon" 
              onClick={() => {
                console.log("Settings button clicked, opening settings dialog");
                setShowSettings(true);
              }}
              title="Settings"
            >
              ⚙️
            </button>
          </div>
        </div>
      </div>
      
      <Settings 
        isOpen={showSettings}
        onClose={() => {
          console.log("Settings: onClose callback triggered");
          setShowSettings(false);
          window.dispatchEvent(new CustomEvent('settingsDialogClosed', { 
            detail: { settingsShown: true } 
          }));
        }}
        onSave={handleSettingsSave}
      >
        {/* Add StorageStats component to Settings */}
        <StorageStats />
      </Settings>
      
      <div className="main-content">
        {error && (
          <div className="error-message">
            {error}
            {contextUtilization > 80 && (
              <span> You're at <strong>{contextUtilization}%</strong> of your context limit with the <strong>{contextLevel}</strong> setting.</span>
            )}
          </div>
        )}
        {showContextWarning && (
          <ContextWarning 
            isVisible={true}
            contextLevel={contextLevel}
            percentage={contextUtilization}
            onOpenSettings={() => {
              setShowSettings(true);
              setTimeout(() => {
                const contextSelect = document.getElementById('context-level-select');
                if (contextSelect) {
                  contextSelect.focus();
                }
              }, 100);
            }}
          />
        )}
        {showResourceWarning && enableResourceWarnings && (
          <ResourceWarning
            isVisible={true}
            percentage={resourceUsage}
            onClose={() => setShowResourceWarning(false)}
          />
        )}
        {topics && activeTopic && (
          <ChatDialog
            topic={topics.find((t) => t.id === activeTopic)}
            onMessageSubmit={handleMessageSubmit}
            isLoading={isLoading}
            searchTerm={searchTerm}
            messageInputRef={messageInputRef}
          />
        )}
        {topics.length === 0 && !activeTopic && !isTopicsLoading && (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: 'calc(100vh - 150px)',
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '72px',
              marginBottom: '24px'
            }}>
              🚀
            </div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#2d3748',
              marginBottom: '16px'
            }}>
              Welcome to Blue Comet- The best FREE web client for Anthropic / Claude
            </h2>
            <p style={{
              color: '#718096',
              maxWidth: '500px',
              marginBottom: '24px',
              lineHeight: '1.6'
            }}>
              Get started by with your FREE TRIAL by clicking the "New Topic" button in the sidebar to begin a new conversation with Claude.
            </p>
            <button 
              onClick={createNewTopic}
              style={{ 
                backgroundColor: '#fff', 
                color: '#0078ff', 
                border: '1px solid #0078ff',
                padding: '10px 20px', 
                borderRadius: '8px', 
                cursor: 'pointer',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f8ff';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#fff';
              }}
            >
              Start New Conversation
            </button>
          </div>
        )}
        
        {/* Add the PWA Install Prompt */}
        <InstallPrompt />
      </div>
    </div>
  );
}

export default App;